/**
 * Pure state helpers for ONE ordered tenant-source override (a carousel or a game list), the
 * client-side view the `useOrderedSource` hook drives. Copy-free, no React, no network: every
 * helper returns a fresh {@link OrderedSourceSnapshot} so the hook's transitions stay testable
 * without rendering. The unified backend contract lives in `./creatives-tenant-source`.
 */
import type { TenantSourcePublished, TenantSourceReverted, TenantSourceState } from './creatives-tenant-source';
import { sameOrder } from './site-games';

/** The client-side state of one ordered source: the working DRAFT order, the PUBLISHED (live) and
 *  PREV_PUBLISHED (one-step-revert) orders, the source's own LIVE order (deployed data-source order),
 *  whether a revert is possible, and whether the source allows a tenant override at all. The
 *  camelCase mirror of {@link TenantSourceState} minus its ref (kind/source_id, owned by the hook). */
export interface OrderedSourceSnapshot {
  /** The working DRAFT order, or `null` when there is no active draft (show the baseline). An EMPTY
   *  array is a real draft: the owner cleared the order (a carousel "clear to nothing"), which must be
   *  distinct from `null` so it is shown and reads as dirty rather than snapping back to the baseline. */
  draft: string[] | null;
  published: string[];
  prevPublished: string[];
  live: string[];
  canRevert: boolean;
  allowTenantSources: boolean;
  /** The source is the whole game catalogue under another name: not a list to arrange (see `TenantSourceState`). */
  catalogue?: boolean;
}

/** An all-empty snapshot: what a disabled / not-yet-loaded source reads as. `draft` is `null` (no active
 *  draft), not `[]` (which would mean an explicit clear). */
export const emptySnapshot = (): OrderedSourceSnapshot => ({
  draft: null,
  published: [],
  prevPublished: [],
  live: [],
  canRevert: false,
  allowTenantSources: false,
});

/** Map the backend {@link TenantSourceState} onto the client snapshot (snake_case -> camelCase). A stored
 *  draft that is empty is treated as "no active draft" on load (the backend cannot distinguish a
 *  never-drafted source from a saved-empty one), so a freshly loaded source opens on its baseline. */
export const snapshotFromState = (state: TenantSourceState): OrderedSourceSnapshot => ({
  draft: state.draft.length ? state.draft : null,
  published: state.published,
  prevPublished: state.prev_published,
  live: state.live,
  canRevert: state.can_revert,
  allowTenantSources: state.allow_tenant_sources,
  ...(state.catalogue ? { catalogue: true } : {}),
});

/** How many games a list shows on the site: its live order, as the service evaluates it; null when that is unknown or
 *  empty. A chip's count, before and after the list is opened: the rows the site loaded for a carousel are fewer. */
export const liveTotal = (s: OrderedSourceSnapshot | undefined): number | null =>
  s && s.live.length > 0 ? s.live.length : null;

/** The order edits are measured against: the published order when there is one; an EMPTY published order
 *  is a real baseline (a cleared carousel) when an override exists (`canRevert`), so publishing/reverting
 *  to empty settles clean instead of snapping to the live order; only an UNCURATED source (no override at
 *  all) falls back to its own live order (so it opens on its real order rather than empty). */
export const baselineOrder = (s: OrderedSourceSnapshot): string[] => {
  if (s.published.length) return s.published;
  return s.canRevert ? [] : s.live;
};

/** The order to show: the working draft when there IS one (including an empty draft, a cleared order),
 *  else the baseline. `null` (not `[]`) is the "no draft" sentinel. */
export const effectiveOrder = (s: OrderedSourceSnapshot): string[] => (s.draft !== null ? s.draft : baselineOrder(s));

/** Whether the shown order differs from the baseline (there are unpublished edits). A `null` draft is never
 *  dirty; an empty draft that differs from a non-empty baseline (a clear) IS dirty. */
export const isDirty = (s: OrderedSourceSnapshot): boolean =>
  s.draft !== null && !sameOrder(s.draft, baselineOrder(s));

/** The snapshot with its working draft replaced (an add / remove / reorder), nothing else touched. */
export const withDraft = (s: OrderedSourceSnapshot, ids: string[]): OrderedSourceSnapshot => ({ ...s, draft: ids });

/** What the site shows once `placed` is the published order, when the service could not read it back. For a source whose
 *  override only orders its own games (`keepsGames`: a game list), those games first, then the rest of the order it
 *  showed before, as the override leaves the rest of the source in (so the count holds); for one whose override is the
 *  whole selection (a carousel's banners), the order placed. With nothing placed, `none`. */
const liveAfterWrite = (placed: string[], before: string[], keepsGames: boolean, none: string[]): string[] => {
  if (!placed.length) return none;
  return keepsGames ? [...placed, ...before.filter((id) => !placed.includes(id))] : [...placed];
};

/** The snapshot after a publish: the newly published order becomes both draft and published, the order
 *  it replaced is kept as the revert target, the live order moves to the returned one, and `canRevert`
 *  reflects whether a differing previous order now exists. A response with no live order (the service
 *  could not read it back) leaves the order just published as the live one (see `liveAfterWrite`): the old
 *  order alone is stale, and kept, it would mark the list unpublished again, inviting a second publish that
 *  overwrites the revert target. Only a cleared order (none published, none returned) keeps the live order it
 *  had. */
export const afterPublish = (
  s: OrderedSourceSnapshot,
  r: TenantSourcePublished,
  keepsGames = true,
): OrderedSourceSnapshot => ({
  ...s,
  draft: r.published,
  published: r.published,
  prevPublished: s.published,
  live: r.live.length ? r.live : liveAfterWrite(r.published, s.live, keepsGames, s.live),
  canRevert: r.can_revert,
});

/** The snapshot after a revert: a copy of the restored orders the backend returns. An EMPTY restored draft
 *  means the revert landed on NO override (a first-publish undo deletes the row), which has no active draft
 *  — mapped to `null` so the source shows its baseline (its live order) and reads clean, not an empty
 *  "cleared" order that reads dirty forever. A response with no live order, as after a publish, leaves the
 *  restored published order as the live one (see `liveAfterWrite`), so the list reads clean. With no published
 *  order either (the source's own order, not read back): for a game list the live order it had stands for the
 *  same games, as an empty one would read as a list that shows none; a carousel's order before is the selection
 *  just undone, and is not kept. `r` carries no `allow_tenant_sources`, so that flag is preserved from the
 *  current snapshot (via the spread). */
export const afterRevert = (
  s: OrderedSourceSnapshot,
  r: TenantSourceReverted,
  keepsGames = true,
): OrderedSourceSnapshot => ({
  ...s,
  draft: r.draft.length ? r.draft : null,
  published: r.published,
  prevPublished: r.prev_published,
  live: r.live.length ? r.live : liveAfterWrite(r.published, s.live, keepsGames, keepsGames ? s.live : []),
  canRevert: r.can_revert,
});
